import React, { useState } from 'react';
import { useInViewOnce } from '../../../lib/useInViewOnce';
import styles from './OrderAttestationStep.module.scss';

/** The served wording both order attestations share. See `RescueDeviceOrderAttestation` in web. */
export interface OrderAttestationTerms {
  verifyHeading: string;
  verifyBullets: string[];
  understandHeading: string;
  understandBullets: string[];
  agreementLabel: string;
  scopeNote?: string | null;
}

interface OrderAttestationStepProps {
  terms: OrderAttestationTerms;
  orgName: string;
  /** The recap row above the terms, e.g. "10" and "Fi Minis with hard cases". */
  recapCount: number;
  recapLabel: string;
  /** One-line shipping summary, e.g. "480 Shelter Way, Austin TX". */
  addressSummary: string;
  onEdit: () => void;
  agreed: boolean;
  onAgreedChange: (agreed: boolean) => void;
}

const RESCUE_NAME_PLACEHOLDER = '{rescue}';
const SCROLL_HINT_ID = 'order-attestation-scroll-hint';

/** Falls back to a generic subject so the heading never reads "I verify that will:". */
function withRescueName(text: string, orgName: string) {
  return text.replaceAll(RESCUE_NAME_PLACEHOLDER, orgName.trim() || 'our rescue');
}

/**
 * Renders the `**phrase**` spans the served copy marks for emphasis. `<strong>` inherits its group's
 * colour, which is what the design does too — the emphasised phrase in the terms group stays grey, it
 * just goes bold.
 */
function withEmphasis(text: string) {
  return text
    .split(/\*\*(.+?)\*\*/g)
    .map((part, index) => (index % 2 === 1 ? <strong key={part}>{part}</strong> : part));
}

/**
 * Step 2 of a rescue order: the commitments the rescue is agreeing to, above a single checkbox that
 * gates submit.
 *
 * Every word here is served alongside the version id it's recorded under, so this renders what it's
 * given and never carries its own copy — that's what makes it impossible to show one wording while
 * recording another. Only the rescue's name and the emphasis markup are resolved locally.
 */
export default function OrderAttestationStep({
  terms,
  orgName,
  recapCount,
  recapLabel,
  addressSummary,
  onEdit,
  agreed,
  onAgreedChange,
}: OrderAttestationStepProps) {
  const headingParts = terms.verifyHeading.split(RESCUE_NAME_PLACEHOLDER);
  const rescueName = orgName.trim() || 'our rescue';

  // The checkbox stays inert until the end of the terms has been in view, so nobody can agree to
  // copy the box never showed them. It's a sentinel rather than scroll arithmetic because the same
  // signal answers both questions: terms short enough to fit have their end in view from the first
  // frame, which lifts the gate immediately, so this only ever bites when the copy actually scrolls.
  const [readToEnd, setReadToEnd] = useState(false);
  const endOfTermsRef = useInViewOnce<HTMLDivElement>(() => setReadToEnd(true), 0);
  // An agreement carried in from an earlier pass (Edit → Continue on an unchanged order) is not
  // re-gated: it was given after reaching the end once, and re-locking the row would leave a ticked
  // box the rescue can't untick.
  const canAgree = readToEnd || agreed;

  return (
    <div className={styles.attestationStep}>
      <p className={styles.description}>
        Read and sign to place this order.
        {terms.scopeNote ? ` ${terms.scopeNote}` : ''}
      </p>

      <div className={styles.attestationRecap}>
        <span className={styles.attestationRecapCount}>{recapCount}</span>
        <span className={styles.attestationRecapDetail}>
          <span className={styles.attestationRecapLabel}>{recapLabel}</span>
          <span className={styles.attestationRecapAddress}>{addressSummary}</span>
        </span>
        <button type="button" className={styles.attestationEditButton} onClick={onEdit}>
          Edit
        </button>
      </div>

      <div className={styles.attestationTerms}>
        {/*
          Focusable, so the terms can be scrolled with the keyboard. Without it the gate below is
          unreachable for anyone not using a pointer — there'd be no way to move this box at all.
        */}
        <div className={styles.attestationTermsScroll} tabIndex={0} role="group" aria-label="Program commitment terms">
          <div className={styles.attestationGroupHeading}>
            {headingParts[0]}
            {headingParts.length > 1 && <span className={styles.attestationOrgName}>{rescueName}</span>}
            {headingParts.slice(1).join(RESCUE_NAME_PLACEHOLDER)}
          </div>
          <ul className={`${styles.attestationList} ${styles.attestationCommitments}`}>
            {terms.verifyBullets.map((bullet) => (
              <li key={bullet}>{withEmphasis(bullet)}</li>
            ))}
          </ul>

          <div className={styles.attestationGroupHeading}>{terms.understandHeading}</div>
          <ul className={`${styles.attestationList} ${styles.attestationConsequences}`}>
            {terms.understandBullets.map((bullet) => (
              <li key={bullet}>{withEmphasis(bullet)}</li>
            ))}
          </ul>

          <div ref={endOfTermsRef} className={styles.attestationTermsEnd} aria-hidden="true" />
        </div>
      </div>

      {/*
        `aria-disabled` rather than `disabled`: a disabled button drops out of the tab order, which
        would hide the very hint that explains what's left to do.
      */}
      <button
        type="button"
        className={`${styles.orderCheckboxRow} ${canAgree ? '' : styles.orderCheckboxRowLocked}`}
        onClick={() => {
          if (!canAgree) {
            return;
          }
          onAgreedChange(!agreed);
        }}
        aria-pressed={agreed}
        aria-disabled={!canAgree}
        aria-describedby={canAgree ? undefined : SCROLL_HINT_ID}
      >
        <span className={`${styles.orderCheckbox} ${agreed ? styles.orderCheckboxChecked : ''}`} aria-hidden="true">
          {agreed && (
            <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M3.5 8.5L6.5 11.5L12.5 4.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>
        <span>
          <span className={styles.orderCheckboxLabel}>{withRescueName(terms.agreementLabel, orgName)}</span>
          {!canAgree && (
            <span className={styles.orderCheckboxHint} id={SCROLL_HINT_ID}>
              Scroll to the end of the terms to agree
            </span>
          )}
        </span>
      </button>
    </div>
  );
}
